/**
 * Open Vest positions with their TP / SL, for the `.VEST` chart lines (#636).
 *
 * Fed by the SAME `broker:positions` pushes the futures brokerStore listens
 * to, but kept apart from it: brokerStore hides every non-tradovate broker
 * (brokerVisibility.ts) and must stay that way, so a Vest row can never reach
 * a CME chart's position / order lines or an order path. Only rows tagged
 * `venue: 'vest'` are kept (see utils/vestPositionLines.ts).
 *
 * No request of its own: the rows come from tradovate-service's
 * VestPositionWatcher (5 s while open, at once after an order action) via the
 * engine relay, which also replays its cached positions to a new socket.
 */

import { useMemo } from 'react';
import { create } from 'zustand';
import { wsService } from '../services/WebSocketService';
import {
  applyVestPositionsEvent,
  removeVestBroker,
  type VestPositionRow,
  type VestPositionsByBroker,
} from '../utils/vestPositionLines';

interface VestPositionsState {
  byBroker: VestPositionsByBroker;
  applyPositions: (payload: { brokerId?: unknown; positions?: unknown; success?: unknown }) => void;
  removeBroker: (brokerId: string) => void;
  reset: () => void;
}

export const useVestPositionsStore = create<VestPositionsState>((set, get) => ({
  byBroker: {},
  applyPositions: (payload) => {
    const prev = get().byBroker;
    const next = applyVestPositionsEvent(prev, payload);
    if (next !== prev) set({ byBroker: next });
  },
  removeBroker: (brokerId) => {
    const prev = get().byBroker;
    const next = removeVestBroker(prev, brokerId);
    if (next !== prev) set({ byBroker: next });
  },
  reset: () => set({ byBroker: {} }),
}));

const EMPTY: readonly VestPositionRow[] = [];

/** Every open Vest row, all brokers. Stable while nothing Vest changed. */
export function useVestPositionRows(enabled: boolean): readonly VestPositionRow[] {
  const byBroker = useVestPositionsStore(s => s.byBroker);
  return useMemo(() => {
    if (!enabled) return EMPTY;
    const all = Object.values(byBroker).flat();
    return all.length === 0 ? EMPTY : all;
  }, [byBroker, enabled]);
}

let handlersRegistered = false;

const onPositions = (data: { brokerId?: unknown; positions?: unknown; success?: unknown }) => {
  useVestPositionsStore.getState().applyPositions(data);
};

const onDisconnected = (data: { brokerId?: unknown }) => {
  if (typeof data?.brokerId === 'string') useVestPositionsStore.getState().removeBroker(data.brokerId);
};

/**
 * Must be called before a forced reconnect so the handlers are re-registered
 * on the new socket. Also drops every row: a broker that disconnected during
 * the gap is simply missing from the engine's replay (no empty push), so its
 * lines would otherwise stay up. The replay repopulates the rest at once.
 */
export function resetVestPositionSocketHandlers(): void {
  handlersRegistered = false;
  useVestPositionsStore.getState().reset();
}

export function initVestPositionSocketHandlers(): void {
  if (handlersRegistered) return;
  handlersRegistered = true;
  wsService.on('broker:positions', onPositions);
  wsService.on('broker:disconnected', onDisconnected);
}
